import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UploadCloud, Grid3x3, Zap, MousePointerClick, Keyboard, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { engine } from "@/audio/engine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SLICE_COLORS = ["#00e5ff", "#ff007f", "#ffd166", "#a855f7", "#00ff88", "#ff8c42", "#22d3ee", "#f472b6"];
const PAD_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8"];
const WAVE_H = 150;

// Peak envelope for drawing.
function computePeaks(buffer, width) {
  const data = buffer.getChannelData(0);
  const step = Math.floor(data.length / width) || 1;
  const peaks = new Array(width).fill(0);
  for (let i = 0; i < width; i++) {
    let max = 0;
    const start = i * step;
    const end = Math.min(start + step, data.length);
    for (let j = start; j < end; j++) {
      const v = Math.abs(data[j]);
      if (v > max) max = v;
    }
    peaks[i] = max;
  }
  return peaks;
}

// Transient detection — returns cut times (seconds) between slices.
function detectTransients(buffer, sensitivity, minGapSec) {
  const data = buffer.getChannelData(0);
  const sr = buffer.sampleRate;
  const win = Math.max(1, Math.floor(sr * 0.01)); // 10ms windows
  const env = [];
  for (let i = 0; i < data.length; i += win) {
    let sum = 0;
    const end = Math.min(i + win, data.length);
    for (let j = i; j < end; j++) sum += Math.abs(data[j]);
    env.push(sum / (end - i || 1));
  }
  const max = Math.max(...env) || 1;
  const thr = (1 - sensitivity) * 0.5 + 0.04;
  const riseThr = (1 - sensitivity) * 0.16 + 0.01;
  const minGapWin = Math.floor((minGapSec * sr) / win);
  const cuts = [];
  let last = -minGapWin;
  for (let k = 1; k < env.length; k++) {
    const rise = (env[k] - env[k - 1]) / max;
    if (env[k] / max > thr && rise > riseThr && k - last >= minGapWin) {
      cuts.push((k * win) / sr);
      last = k;
    }
  }
  return cuts;
}

export default function SampleChopper({ source }) {
  const canvasRef = useRef(null);
  const dragRef = useRef(null); // index of cut being dragged
  const [buffer, setBuffer] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("grid");
  const [sliceCount, setSliceCount] = useState(8);
  const [sensitivity, setSensitivity] = useState(0.5);
  const [cuts, setCuts] = useState([]);
  const [padPage, setPadPage] = useState(0);
  const [kbOn, setKbOn] = useState(false);
  const [hitPad, setHitPad] = useState(-1);
  const fileRef = useRef(null);
  const sensTimer = useRef(null);

  const dur = buffer ? buffer.duration : 0;

  // Load from an external source (a separated stem) or a file.
  const loadBuffer = useCallback(async (buf, nm) => {
    setBuffer(buf);
    setName(nm);
    setPadPage(0);
    // default to grid on load
    setMode("grid");
    const n = 8;
    setSliceCount(n);
    const c = [];
    for (let i = 1; i < n; i++) c.push((buf.duration * i) / n);
    setCuts(c);
  }, []);

  useEffect(() => {
    if (!source || !source.url) return;
    let cancelled = false;
    setLoading(true);
    engine
      .decodeUrl(`${BACKEND_URL}${source.url}`)
      .then((buf) => {
        if (!cancelled) loadBuffer(buf, source.name || "Stem");
      })
      .catch(() => toast.error("Couldn't load that stem to chop"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [source, loadBuffer]);

  const handleFile = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const buf = await engine.decodeFile(file);
      loadBuffer(buf, file.name.replace(/\.[^.]+$/, ""));
    } catch (e) {
      toast.error("Couldn't read that audio file");
    }
    setLoading(false);
  };

  // Recompute cuts when mode / sliceCount / sensitivity changes.
  const applyGrid = (n) => {
    if (!buffer) return;
    const c = [];
    for (let i = 1; i < n; i++) c.push((buffer.duration * i) / n);
    setCuts(c);
  };

  const changeMode = (m) => {
    setMode(m);
    if (!buffer) return;
    if (m === "grid") applyGrid(sliceCount);
    else if (m === "auto") setCuts(detectTransients(buffer, sensitivity, 0.15));
    else if (m === "manual") setCuts([]); // start fresh
  };

  const onSensitivity = (v) => {
    setSensitivity(v);
    if (sensTimer.current) clearTimeout(sensTimer.current);
    sensTimer.current = setTimeout(() => {
      if (buffer && mode === "auto") setCuts(detectTransients(buffer, v, 0.15));
    }, 120);
  };

  // Slices derived from cuts.
  const slices = useMemo(() => {
    if (!buffer) return [];
    const bounds = [0, ...cuts, dur];
    const out = [];
    for (let i = 0; i < bounds.length - 1; i++) {
      out.push({ start: bounds[i], end: bounds[i + 1], idx: i });
    }
    return out;
  }, [cuts, buffer, dur]);

  const totalPages = Math.max(1, Math.ceil(slices.length / 8));
  const pageSlices = slices.slice(padPage * 8, padPage * 8 + 8);

  const playSliceByIndex = useCallback(
    (slice, padPos) => {
      if (!buffer || !slice) return;
      engine.playSlice(buffer, slice.start, slice.end - slice.start, 1);
      setHitPad(padPos);
      setTimeout(() => setHitPad((p) => (p === padPos ? -1 : p)), 140);
    },
    [buffer]
  );

  // Keyboard triggering (toggleable, ignores typing in inputs).
  useEffect(() => {
    if (!kbOn) return;
    const isTyping = () => {
      const el = document.activeElement;
      if (!el) return false;
      const t = el.tagName;
      return t === "INPUT" || t === "TEXTAREA" || t === "SELECT" || el.isContentEditable;
    };
    const down = (e) => {
      if (e.repeat || isTyping()) return;
      const pos = PAD_KEYS.indexOf(e.key);
      if (pos >= 0 && pageSlices[pos]) {
        e.preventDefault();
        playSliceByIndex(pageSlices[pos], pos);
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [kbOn, pageSlices, playSliceByIndex]);

  // ---- waveform drawing ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !buffer) return;
    const rect = canvas.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = WAVE_H;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, w, h);

    // slice backgrounds
    const bounds = [0, ...cuts, dur];
    for (let i = 0; i < bounds.length - 1; i++) {
      const x0 = (bounds[i] / dur) * w;
      const x1 = (bounds[i + 1] / dur) * w;
      ctx.fillStyle = SLICE_COLORS[i % SLICE_COLORS.length] + "18";
      ctx.fillRect(x0, 0, x1 - x0, h);
    }

    // waveform
    const peaks = computePeaks(buffer, w);
    ctx.fillStyle = "#e5e5e5";
    for (let x = 0; x < w; x++) {
      const p = peaks[x];
      const barH = Math.max(1, p * (h * 0.9));
      ctx.fillRect(x, (h - barH) / 2, 1, barH);
    }

    // cut boundary lines + slice numbers
    ctx.font = "bold 11px monospace";
    bounds.forEach((t, i) => {
      const x = (t / dur) * w;
      if (i > 0 && i < bounds.length - 1) {
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
        // grab handle
        ctx.fillStyle = "#fff";
        ctx.fillRect(x - 3, h / 2 - 8, 6, 16);
      }
    });
    // slice index labels
    for (let i = 0; i < bounds.length - 1; i++) {
      const x0 = (bounds[i] / dur) * w;
      ctx.fillStyle = SLICE_COLORS[i % SLICE_COLORS.length];
      ctx.fillText(String(i + 1), x0 + 4, 14);
    }
  }, [buffer, cuts, dur]);

  const xToTime = (clientX) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(((clientX - rect.left) / rect.width) * dur, dur));
  };

  const nearestCutIndex = (clientX) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const px = clientX - rect.left;
    let best = -1;
    let bestDist = 7; // px threshold
    cuts.forEach((t, i) => {
      const cx = (t / dur) * rect.width;
      const d = Math.abs(cx - px);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best;
  };

  const onPointerDown = (e) => {
    if (!buffer) return;
    const idx = nearestCutIndex(e.clientX);
    if (idx >= 0) {
      dragRef.current = idx;
    } else if (mode === "manual") {
      const t = xToTime(e.clientX);
      const next = [...cuts, t].sort((a, b) => a - b);
      setCuts(next);
      dragRef.current = next.indexOf(t);
    }
  };

  const onPointerMove = (e) => {
    if (dragRef.current == null || !buffer) return;
    const i = dragRef.current;
    const t = xToTime(e.clientX);
    const min = i > 0 ? cuts[i - 1] + 0.01 : 0.01;
    const max = i < cuts.length - 1 ? cuts[i + 1] - 0.01 : dur - 0.01;
    const next = [...cuts];
    next[i] = Math.max(min, Math.min(t, max));
    setCuts(next);
  };

  const onPointerUp = () => {
    if (dragRef.current != null) {
      dragRef.current = null;
      setCuts((c) => [...c].sort((a, b) => a - b));
    }
  };

  const onDoubleClick = (e) => {
    if (!buffer) return;
    const idx = nearestCutIndex(e.clientX);
    if (idx >= 0) setCuts(cuts.filter((_, i) => i !== idx));
  };

  if (!buffer && !loading) {
    return (
      <div className="mt-6" data-testid="sample-chopper">
        <div className="flex items-center gap-2 mb-3">
          <span className="hw-label text-[10px] text-neutral-400">Sample Chopper</span>
          <span className="text-[10px] text-neutral-600">— slice any sound into playable pads</span>
        </div>
        <button
          data-testid="chopper-upload-btn"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFile(e.dataTransfer.files?.[0]);
          }}
          className="w-full rounded-lg border-2 border-dashed border-[#2a2a2a] hover:border-[#00e5ff] transition-colors py-8 flex flex-col items-center justify-center gap-2 bg-[#0a0a0a]"
        >
          <UploadCloud size={26} className="text-neutral-500" />
          <span className="text-neutral-300 text-sm font-semibold">Drop a loop/stem to chop</span>
          <span className="text-neutral-600 text-xs">or use the ✂ Chop button on a stem above</span>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          className="hidden"
          data-testid="chopper-file-input"
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="sample-chopper">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className="hw-label text-[10px] text-neutral-400 truncate">Chopping: "{name}"</span>
        <button
          data-testid="chopper-new-btn"
          onClick={() => {
            setBuffer(null);
            setCuts([]);
          }}
          className="text-[10px] text-neutral-500 hover:text-neutral-300 flex items-center gap-1"
        >
          <Trash2 size={11} /> New sample
        </button>
      </div>

      {loading ? (
        <div className="py-10 flex items-center justify-center text-neutral-400 gap-2">
          <Loader2 size={18} className="animate-spin" /> Loading sample…
        </div>
      ) : (
        <>
          {/* control toolbar */}
          <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="chopper-toolbar">
            {[
              { id: "grid", label: "Grid", icon: Grid3x3 },
              { id: "auto", label: "Auto", icon: Zap },
              { id: "manual", label: "Manual", icon: MousePointerClick },
            ].map((m) => {
              const Icon = m.icon;
              const active = mode === m.id;
              return (
                <button
                  key={m.id}
                  data-testid={`chopper-mode-${m.id}`}
                  onClick={() => changeMode(m.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-bold transition-colors ${
                    active ? "border-[#00e5ff] text-[#00e5ff] bg-[#00e5ff]/10" : "border-[#333] text-neutral-300 hover:border-[#555]"
                  }`}
                >
                  <Icon size={13} /> {m.label}
                </button>
              );
            })}

            <div className="h-5 w-px bg-[#333] mx-1" />

            {/* dynamic sub-controls */}
            {mode === "grid" && (
              <div className="flex items-center gap-1" data-testid="chopper-grid-controls">
                <span className="text-[10px] text-neutral-500 mr-1">Slices</span>
                {[4, 8, 16, 32].map((n) => (
                  <button
                    key={n}
                    data-testid={`chopper-slices-${n}`}
                    onClick={() => {
                      setSliceCount(n);
                      applyGrid(n);
                      setPadPage(0);
                    }}
                    className={`w-8 h-7 rounded-sm border text-[11px] font-bold transition-colors ${
                      sliceCount === n ? "border-[#ffd166] text-[#ffd166] bg-[#ffd166]/10" : "border-[#333] text-neutral-400 hover:border-[#555]"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}
            {mode === "auto" && (
              <div className="flex items-center gap-2 flex-1 min-w-[180px]" data-testid="chopper-auto-controls">
                <span className="text-[10px] text-neutral-500 whitespace-nowrap">Sensitivity</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={sensitivity}
                  onChange={(e) => onSensitivity(parseFloat(e.target.value))}
                  data-testid="chopper-sensitivity"
                  className="flex-1 accent-[#00e5ff] max-w-[220px]"
                />
                <span className="font-mono-data text-[10px] text-neutral-400 w-8">{sensitivity.toFixed(2)}</span>
              </div>
            )}
            {mode === "manual" && (
              <span className="text-[10px] text-neutral-500" data-testid="chopper-manual-hint">
                Click the waveform to drop a chop · double-click a line to remove it
              </span>
            )}

            <div className="flex-1" />

            <button
              data-testid="chopper-kb-toggle"
              onClick={() => setKbOn((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-bold transition-colors ${
                kbOn ? "border-[#00ff88] text-[#00ff88] bg-[#00ff88]/10" : "border-[#333] text-neutral-400 hover:border-[#555]"
              }`}
              title="Play pads with your keyboard keys 1–8"
            >
              <Keyboard size={13} /> Keys {kbOn ? "ON" : "OFF"}
            </button>
          </div>

          {/* waveform */}
          <canvas
            ref={canvasRef}
            data-testid="chopper-waveform"
            style={{ width: "100%", height: WAVE_H, touchAction: "none", cursor: mode === "manual" ? "crosshair" : "ew-resize" }}
            className="rounded-md border border-[#262626] bg-[#080808]"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onDoubleClick={onDoubleClick}
          />

          {/* pad pager */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-3" data-testid="chopper-pager">
              {Array.from({ length: totalPages }).map((_, p) => (
                <button
                  key={p}
                  data-testid={`chopper-page-${p}`}
                  onClick={() => setPadPage(p)}
                  className={`px-2.5 py-1 rounded-sm text-[10px] font-bold border ${
                    padPage === p ? "border-[#00e5ff] text-[#00e5ff]" : "border-[#333] text-neutral-500"
                  }`}
                >
                  {p * 8 + 1}–{Math.min(p * 8 + 8, slices.length)}
                </button>
              ))}
            </div>
          )}

          {/* pads */}
          <div className="grid grid-cols-4 gap-2.5 mt-3" data-testid="chopper-pads">
            {Array.from({ length: 8 }).map((_, i) => {
              const slice = pageSlices[i];
              const globalIdx = padPage * 8 + i;
              const color = SLICE_COLORS[globalIdx % SLICE_COLORS.length];
              const active = hitPad === i;
              return (
                <button
                  key={i}
                  data-testid={`chopper-pad-${i}`}
                  disabled={!slice}
                  onPointerDown={() => slice && playSliceByIndex(slice, i)}
                  className="relative h-16 rounded-md border font-bold transition-all disabled:opacity-25 disabled:cursor-not-allowed select-none"
                  style={{
                    borderColor: slice ? color + (active ? "" : "66") : "#222",
                    background: active ? color + "44" : slice ? color + "14" : "#0a0a0a",
                    boxShadow: active ? `0 0 18px ${color}88` : "none",
                    color: slice ? color : "#444",
                  }}
                >
                  <span className="absolute top-1.5 left-2 text-[11px]">{slice ? globalIdx + 1 : "—"}</span>
                  {kbOn && slice && (
                    <span className="absolute bottom-1.5 right-2 text-[10px] font-mono-data opacity-80">{PAD_KEYS[i]}</span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-neutral-600 mt-2">
            Tap a pad to play its slice — it runs through your mixer (EQ/FX/master apply). Turn on <span className="text-[#00ff88]">Keys</span> to play them from your keyboard.
          </p>
        </>
      )}
    </div>
  );
}
